import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import { readGuestSession } from "../lib/guestSession";

const DISMISS_KEY = "bc_guest_return_banner_dismissed";

/**
 * Soft, dismissible retargeting nudge shown on the public HomePage when a
 * `bc_guest_session_v1` blob exists in localStorage. Invites returning
 * guests to resume the practice they started AND to create an account so
 * their progress is preserved past task 4.
 *
 * Dismissal is per-session (sessionStorage) — the nudge comes back next
 * time they visit until they either sign up (guest session is cleared by
 * `migrateGuestSessionIfPresent`) or start over.
 */
export const GuestReturnBanner = () => {
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    try {
      const dismissedInTab = sessionStorage.getItem(DISMISS_KEY) === "1";
      if (dismissedInTab) {
        setDismissed(true);
        return;
      }
    } catch { /* ignore */ }
    const s = readGuestSession();
    if (s && (s.last_task_number || 0) >= 1) {
      setSession(s);
    }
  }, []);

  if (dismissed || !session) return null;

  const taskNum = session.last_task_number || 1;
  const handleContinue = () => {
    navigate("/intervention/dial_down_tension?guest=1");
  };
  const handleCreate = () => {
    try {
      sessionStorage.setItem(
        "post_auth_redirect",
        "/intervention/dial_down_tension"
      );
    } catch { /* ignore */ }
    navigate("/signup");
  };
  const handleDismiss = () => {
    try { sessionStorage.setItem(DISMISS_KEY, "1"); } catch { /* ignore */ }
    setDismissed(true);
  };

  return (
    <div
      className="w-full bg-gradient-to-r from-[#EAF3F6] via-[#F0EAF6] to-[#FBF6EE] border-b border-[#3090A8]/15"
      data-testid="guest-return-banner"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-10 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <p className="text-sm text-[#1B2349] leading-relaxed">
          <span className="font-semibold">Welcome back — glad you're here.</span>{" "}
          You made it to <span className="font-semibold">Task {taskNum}</span> of Dial Down Body Tension last time. Your progress is safe. Save it for good with a free account.
        </p>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={handleContinue}
            className="px-4 py-2 rounded-full text-sm text-[#1B2349] bg-white border border-[#1B2349]/15 hover:border-[#1B2349]/40 active:scale-[0.98] transition"
            data-testid="guest-return-continue-btn"
          >
            Continue practice
          </button>
          <button
            onClick={handleCreate}
            className="px-4 py-2 rounded-full text-sm bg-[#1B2349] text-white hover:bg-[#0F1838] active:scale-[0.98] transition"
            data-testid="guest-return-signup-btn"
          >
            Create account
          </button>
          <button
            onClick={handleDismiss}
            className="p-2 rounded-full text-[#718096] hover:text-[#1B2349] hover:bg-white/60 transition"
            aria-label="Dismiss"
            data-testid="guest-return-dismiss-btn"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default GuestReturnBanner;
